import { useGameStore } from '../../../store/gameStore';
import { useSettingsStore } from '../../../store/settingsStore';
import { useTelegram } from '../../../hooks/useTelegram';
import { cn } from '../../../lib/cn';

export default function SudokuGrid() {
  const { haptic } = useTelegram();
  const currentGame = useGameStore(state => state.currentGame);
  const selectedCell = useGameStore(state => state.selectedCell);
  const selectCell = useGameStore(state => state.selectCell);
  const errorCells = useGameStore(state => state.errorCells);
  const aiHintCell = useGameStore(state => state.aiHintCell);
  const getCellNotes = useGameStore(state => state.getCellNotes);
  const isNoteMode = useGameStore(state => state.isNoteMode);
  useGameStore(state => state.cellNotes); // Subscribe to cellNotes changes for reactivity
  const highlightSimilar = useSettingsStore(state => state.highlightSimilar);

  if (!currentGame) return null;

  const handleCellClick = (row: number, col: number) => {
    haptic('light');
    selectCell(row, col);
  };

  const selectedValue = selectedCell
    ? currentGame.currentState[selectedCell.row][selectedCell.col]
    : null;
  
  // Check if selected cell has a value (not empty)
  const hasSelectedValue = selectedValue !== null && selectedValue !== 0;

  return (
    <div className="sudoku-grid bg-card border-2 border-border rounded-lg p-2 shadow-lg">
      <div className="grid grid-cols-9 gap-0">
        {currentGame.currentState.map((row, rowIndex) =>
          row.map((value, colIndex) => {
            const isSelected = 
              selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
            const isGiven = currentGame.puzzle[rowIndex][colIndex] !== 0;
            const isSameRow = selectedCell?.row === rowIndex;
            const isSameCol = selectedCell?.col === colIndex;
            const isSameBox = 
              selectedCell &&
              Math.floor(selectedCell.row / 3) === Math.floor(rowIndex / 3) &&
              Math.floor(selectedCell.col / 3) === Math.floor(colIndex / 3);
            const isSimilarValue = 
              highlightSimilar && value !== 0 && value === selectedValue;
            
            // Highlight cells with same value as selected (when selected cell has a value)
            // This should work for ALL cells (given and user-entered) with the same value
            const hasSameValue = hasSelectedValue && value !== 0 && value === selectedValue && !isSelected;

            const isThickRightBorder = (colIndex + 1) % 3 === 0 && colIndex !== 8;
            const isThickBottomBorder = (rowIndex + 1) % 3 === 0 && rowIndex !== 8;
            const cellKey = `${rowIndex}-${colIndex}`;
            const isError = errorCells.has(cellKey);
            const isAIHint = aiHintCell?.row === rowIndex && aiHintCell?.col === colIndex;
            const cellNotes = !isGiven && value === 0 && !isAIHint ? getCellNotes(rowIndex, colIndex) : new Set<number>();
            const hasNotes = cellNotes.size > 0;

            return (
              <button
                key={`${rowIndex}-${colIndex}`}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                disabled={currentGame.status === 'completed'}
                className={cn(
                  'aspect-square flex items-center justify-center relative',
                  'border border-foreground/30 transition-all',
                  'active:scale-95',
                  isThickRightBorder && 'border-r-2 border-r-foreground',
                  isThickBottomBorder && 'border-b-2 border-b-foreground',
                  // Selected cells (highest priority for non-given cells)
                  !isGiven && isSelected && !isNoteMode && '!bg-blue-500/50',
                  !isGiven && isSelected && isNoteMode && '!bg-purple-500/50',
                  // Given cells: gray background and text (only if not selected)
                  isGiven && !isSelected && 'bg-muted text-muted-foreground font-bold cursor-default',
                  // AI Hint cells: pink gradient background matching button/toast, no ring
                  !isGiven && !isSelected && isAIHint && 'bg-gradient-to-br from-pink-400 to-rose-400 text-white font-bold',
                  // Same row/col/box highlighting (only if not selected and not given)
                  !isGiven && !isSelected && !isAIHint && (isSameRow || isSameCol || isSameBox) && 'bg-accent/50',
                  // Similar value highlighting
                  !isGiven && !isSelected && isSimilarValue && 'bg-primary/10',
                  // Error cells: red text
                  !isGiven && !isAIHint && isError && 'text-red-500 font-bold',
                  // User-entered cells: primary color
                  !isGiven && !isAIHint && !isError && 'text-primary hover:bg-accent',
                  currentGame.status === 'completed' && 'cursor-not-allowed'
                )}
              >
                {value !== 0 ? (
                  <span className={cn(
                    // Increase font size by 0.5 for cells with same value as selected
                    hasSameValue ? "text-xl sm:text-2xl" : "text-lg sm:text-xl",
                    // If cell has same value as selected, make it bold and use primary color (works for both given and user-entered)
                    hasSameValue ? "font-bold text-primary" : (isGiven ? "font-bold" : "font-semibold")
                  )}>{value}</span>
                ) : isAIHint ? (
                  <span className="text-lg sm:text-xl font-semibold">{aiHintCell.value}</span>
                ) : hasNotes ? (
                  <div className="grid grid-cols-3 gap-0 w-full h-full p-0.5">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <span
                        key={num}
                        className={cn(
                          'text-[8px] sm:text-[9px] leading-none flex items-center justify-center',
                          cellNotes.has(num) ? 'text-foreground' : 'text-transparent'
                        )}
                      >
                        {num}
                      </span>
                    ))}
                  </div>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

